/**
 * Phase 19 — Enterprise Payment Readiness & Eligibility Gating Matrix
 * Evaluates the 8 mandatory procurement prerequisites before authorizing any financial disbursement.
 */

import { ReadinessChecklistItem } from '@/types/domain/payment-management';
import { InvoiceRecord } from '@/types/domain/invoice-management';

export interface PaymentEligibilityResult {
  isEligible: boolean;
  blockingReasons: string[];
  checklist: ReadinessChecklistItem[];
}

export function evaluatePaymentEligibility(params: {
  invoice?: InvoiceRecord | null;
  hasBankMasterData?: boolean;
  proposedAmount?: number;
  alreadyPaidAmount?: number;
}): PaymentEligibilityResult {
  const { invoice, hasBankMasterData = true, proposedAmount, alreadyPaidAmount = 0 } = params;
  const blockingReasons: string[] = [];
  const checklist: ReadinessChecklistItem[] = [];

  // Check 1: Invoice Exists & Active
  const invoiceActive = Boolean(invoice && invoice.status !== 'CANCELLED' && invoice.status !== 'REJECTED');
  checklist.push({
    id: 'check-inv-active',
    label: 'Authorized Active Vendor Invoice',
    passed: invoiceActive,
    severity: 'BLOCKING',
    details: invoiceActive
      ? `Verified invoice #${invoice?.invoiceNumber} (${invoice?.status}).`
      : 'Invoice does not exist or has been cancelled/rejected.',
  });
  if (!invoiceActive) blockingReasons.push('Invoice is not active or has been cancelled.');

  // Check 2: PO Commitment Verified
  const poValid = Boolean(invoice?.poId && invoice?.poNumber);
  checklist.push({
    id: 'check-po-valid',
    label: 'Commercial PO Commitment Verification',
    passed: poValid,
    severity: 'BLOCKING',
    details: poValid
      ? `Linked to authorized PO #${invoice?.poNumber}.`
      : 'Missing authorized Purchase Order reference.',
  });
  if (!poValid) blockingReasons.push('Invoice lacks an authorized Purchase Order reference.');

  // Check 3: GRN Accepted Stock Verified
  const grnValid = Boolean(invoice?.grnId || invoice?.grnNumber);
  checklist.push({
    id: 'check-grn-valid',
    label: 'Warehouse Goods Receipt (GRN) Inwarding',
    passed: grnValid,
    severity: 'BLOCKING',
    details: grnValid
      ? `Verified against GRN #${invoice?.grnNumber || invoice?.grnId}.`
      : 'Missing warehouse receiving evidence (GRN). Payment blocked until goods are inwarded.',
  });
  if (!grnValid) blockingReasons.push('Missing warehouse Goods Receipt Note (GRN).');

  // Check 4: 3-Way Match Verification
  const matchingStatus =
    (invoice as any)?.matchingStatus ||
    ((invoice as any)?.status === 'ApprovedForPayment' || invoice?.status === 'APPROVED' || invoice?.status === 'READY_FOR_PAYMENT'
      ? 'MATCHED'
      : undefined);
  const matchPassed = matchingStatus === 'MATCHED';
  checklist.push({
    id: 'check-match-passed',
    label: 'Automated 3-Way Financial Match Clearance',
    passed: matchPassed,
    severity: 'BLOCKING',
    details: matchPassed
      ? 'Clean 3-way match verified (PO vs GRN vs Invoice).'
      : `Matching status is ${matchingStatus?.replace(/_/g, ' ') || 'UNMATCHED'}. Reconciliation required.`,
  });
  if (!matchPassed) blockingReasons.push(`3-Way match not completed (${matchingStatus || 'UNMATCHED'}).`);

  // Check 5: No Active Blocking Exceptions
  const unresolvedBlocking = (invoice?.exceptions || []).filter(
    (e) => (e.severity === 'BLOCKING' || e.severity === 'CRITICAL') && e.status !== 'RESOLVED'
  );
  const exceptionsCleared = unresolvedBlocking.length === 0;
  checklist.push({
    id: 'check-exceptions-cleared',
    label: 'Exception Resolution Clearance',
    passed: exceptionsCleared,
    severity: 'BLOCKING',
    details: exceptionsCleared
      ? 'Zero unresolved blocking exceptions on invoice.'
      : `${unresolvedBlocking.length} blocking exception(s) require resolution before disbursement.`,
  });
  if (!exceptionsCleared) {
    unresolvedBlocking.forEach((e) => blockingReasons.push(`Blocking exception: ${e.description}`));
  }

  // Check 6: Accounts Payable Approval Granted
  const isApApproved =
    invoice?.status === 'APPROVED' ||
    (invoice as any)?.status === 'ApprovedForPayment' ||
    invoice?.status === 'READY_FOR_PAYMENT' ||
    invoice?.status === 'PAID';
  checklist.push({
    id: 'check-ap-approved',
    label: 'Accounts Payable Formal Approval',
    passed: isApApproved,
    severity: 'BLOCKING',
    details: isApApproved
      ? 'Finance & Accounts Payable approval granted.'
      : 'Invoice pending final AP review and approval.',
  });
  if (!isApApproved) blockingReasons.push('Invoice has not received formal AP approval.');

  // Check 7: Remaining Payable Balance Available
  const totalAmount = invoice?.totalAmount || 0;
  const remaining = Math.max(0, totalAmount - alreadyPaidAmount);
  const hasRemainingBalance = remaining > 0.01;
  checklist.push({
    id: 'check-balance',
    label: 'Outstanding Payable Balance',
    passed: hasRemainingBalance,
    severity: 'BLOCKING',
    details: hasRemainingBalance
      ? `Remaining payable balance is ${remaining} ${invoice?.currency || 'INR'}.`
      : 'Invoice is already fully settled (Remaining Balance: 0).',
  });
  if (!hasRemainingBalance) blockingReasons.push('Invoice has already been fully paid.');

  // Check 8: Vendor Bank Master Data Available
  checklist.push({
    id: 'check-bank-master',
    label: 'Vendor Beneficiary Banking Master Data',
    passed: hasBankMasterData,
    severity: 'BLOCKING',
    details: hasBankMasterData
      ? 'Verified active bank account, IFSC/SWIFT code on vendor master.'
      : 'Vendor master lacks verified electronic disbursement banking coordinates.',
  });
  if (!hasBankMasterData) blockingReasons.push('Vendor lacks verified bank disbursement details.');

  // Optional Check: Proposed amount does not exceed remaining
  if (proposedAmount !== undefined && proposedAmount > remaining + 0.01) {
    blockingReasons.push(
      `Proposed payment amount (${proposedAmount}) exceeds remaining payable balance (${remaining}).`
    );
  }

  const isEligible = blockingReasons.length === 0;

  return {
    isEligible,
    blockingReasons,
    checklist,
  };
}
